'use client'

import type { RecurrenceRule } from '@ordo/recurrence'
import {
  Calendar as CalendarIcon,
  ChevronUp,
  FolderKanban,
  Image as ImageIcon,
  Loader2,
  Lock,
  PanelRightOpen,
  Paperclip,
  Plus,
  Repeat,
  User,
} from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { useIsMobile } from '../hooks/use-is-mobile'
import { useRevealOnScroll } from '../hooks/use-reveal-on-scroll'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { DateRecurrencePicker } from './date-recurrence-picker'
import { Dialog, DialogContent, DialogTitle } from './dialog'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Sheet, SheetContent, SheetTitle } from './sheet'
import { TaskRelationTrigger } from './task-relation-trigger'

/**
 * Shared task-creation primitive used by every Ordo app that needs to file a
 * Tasker task without leaving its own surface. Headless — takes data +
 * callbacks, does not fetch anything.
 *
 * Three wrappers, one form:
 *   - `AppTaskCreator` — Dialog (desktop) / bottom Sheet (mobile). Used by
 *     HQ's MyTasks widget and any other host that wants a compact popup.
 *   - `AppTaskCreatorPanel` — right-side Sheet on desktop, full-screen Sheet
 *     on mobile. Stacked layout, sticky footer. Pairs with Tasker's detail
 *     panel on project pages.
 *   - `AppTaskCreatorInline` — idle "Add a task" row that expands in place
 *     on focus. No container chrome. Pairs with Tasker's /my-tasks list.
 */

export interface AppTaskCreatorUser {
  id: string
  name: string
  email: string
  image?: string | null
}

export interface AppTaskCreatorProject {
  id: string
  name: string
}

export interface AppTaskCreatorClient {
  id: string
  name: string
  slug: string
  type?: string | null
}

export interface AppTaskCreatorLead {
  recordId: string
  portalId: string
  objectType: string
  name: string
  context: string | null
}

export interface AppTaskCreatorRelationFields {
  title: string
  description: string
  lead: AppTaskCreatorLead | null
  clients: AppTaskCreatorClient[]
  onLeadChange: (lead: AppTaskCreatorLead | null) => void
  onClientsChange: (clients: AppTaskCreatorClient[]) => void
}

export interface AppTaskCreatorSubmitInput {
  title: string
  description?: string
  assigneeId?: string
  projectId?: string
  dueDate?: string // ISO
  /** ISO start date — optional range start for the task. */
  startDate?: string
  /** When true, `dueDate` includes a time-of-day component. */
  hasDueTime?: boolean
  /** Recurrence rule. `null` means "no recurrence"; `undefined` means "host doesn't support it". */
  recurrenceRule?: RecurrenceRule | null
  private: boolean
  leadRecordId?: string
  organizations?: AppTaskCreatorClient[]
}

interface AppTaskCreatorBaseProps {
  users: AppTaskCreatorUser[]
  projects: AppTaskCreatorProject[]

  /**
   * Signed-in user's email. Used to auto-assign to "me" by looking up a
   * matching row in `users`. Right prop for cross-app hosts (HQ, Factory)
   * that don't know the Tasker-side user id.
   */
  currentUserEmail?: string
  /**
   * Direct assignee ID override — wins over `currentUserEmail`. Use when the
   * host already knows the target user's id in the consuming app's id space.
   */
  defaultAssigneeId?: string
  defaultProjectId?: string
  /**
   * Pre-fills the due date pill. `null`/undefined leave it empty. Host-driven
   * so each mount can use its own convention (e.g. a dashboard widget
   * defaults to today; an inline row at the bottom of a list does not).
   */
  defaultDueDate?: Date | null

  /** Optional related-record pickers supplied by the host. */
  renderRelationFields?: (ctx: AppTaskCreatorRelationFields) => React.ReactNode
  /** Create the task with the same validation, then open its detail sidebar. */
  onSubmitAndOpen?: (input: AppTaskCreatorSubmitInput) => Promise<void>

  /** Called on "Create". Resolve the promise to signal success. */
  onSubmit: (input: AppTaskCreatorSubmitInput) => Promise<void>

  /**
   * Host-rendered extra fields (e.g. a project's required custom fields),
   * shown between the built-in fields and the action bar. `showErrors` flips
   * to true after a Create attempt fails `validateExtraFields`, so the host
   * can highlight what's missing.
   */
  renderExtraFields?: (ctx: { projectId: string | null; showErrors: boolean }) => React.ReactNode
  /**
   * Gate for Create/escalate. Return false to block the submit; the form
   * flips `showErrors` on and stays open. Reset when the project changes or
   * a submit succeeds.
   */
  validateExtraFields?: (projectId: string | null) => boolean
}

export interface AppTaskCreatorProps extends AppTaskCreatorBaseProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  /**
   * Legacy callback for the separate sidebar action. The chevron always expands
   * the local fields. When provided, the sidebar button acts as an "Open full editor"
   * escape hatch instead of toggling the local expanded section. Host
   * receives whatever fields the user has already filled and decides what
   * to do (typically: create the task with these defaults and open a full
   * task-detail surface). The local expanded fields remain available.
   */
  onEscalate?: (partial: Partial<AppTaskCreatorSubmitInput>) => void
}

type FormVariant = 'modal' | 'panel' | 'inline'

export interface AppTaskCreatorFormProps extends AppTaskCreatorBaseProps {
  variant?: FormVariant
  /** Fired after `onSubmit` resolves. Modal/panel wrappers use this to close. Ignored for `inline`. */
  onAfterSubmit?: () => void
  /** Fired on Escape for inline. Modal/panel already handle Escape via Sheet/Dialog. */
  onCancel?: () => void
  /** Imperative handle to the title <input>. Inline wrapper uses this to refocus on demand. */
  inputRef?: React.Ref<HTMLInputElement>
  /** Imperative handle to trigger submit programmatically (used by inline wrapper for blur-to-save). */
  submitRef?: React.Ref<AppTaskCreatorFormSubmitHandle>
  /**
   * Escape hatch to the full task editor. When defined, the separate sidebar button
   * calls this with the currently-filled fields instead of
   * toggling the inline expand section. See `AppTaskCreatorProps.onEscalate`.
   */
  onEscalate?: (partial: Partial<AppTaskCreatorSubmitInput>) => void
  autoFocus?: boolean
}

export interface AppTaskCreatorFormSubmitHandle {
  /** Attempt submit. Resolves true if a task was created, false if title was empty or already submitting. */
  submit: () => Promise<boolean>
  /** Whether the title input currently has non-whitespace content. */
  hasContent: () => boolean
}

export interface AppTaskCreatorInlineProps extends AppTaskCreatorBaseProps {
  className?: string
  /** Label shown on the idle row. Defaults to "Add a task". */
  idlePlaceholder?: string
  /** When true, the idle "+ Add a task" row is hidden. Host must drive expansion via the ref handle. */
  hideIdle?: boolean
}

export interface AppTaskCreatorInlineHandle {
  focus: () => void
}

function userInitials(u: AppTaskCreatorUser): string {
  const parts = (u.name ?? u.email).trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

function formatDueLabel(d: Date | null): string {
  if (!d) {
    return 'Due date'
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return (value) => {
    for (const ref of refs) {
      if (!ref) {
        continue
      }
      if (typeof ref === 'function') {
        ref(value)
      } else {
        ;(ref as React.MutableRefObject<T | null>).current = value
      }
    }
  }
}

/**
 * Windowed popover list body shared by the assignee/project pickers below.
 * Renders a chunk of `items` and reveals more on scroll / browser idle via
 * `useRevealOnScroll`, so long people/project lists stay smooth.
 */
function RevealList<T>({
  open,
  resetKey,
  items,
  header,
  renderItem,
  emptyText,
}: {
  open: boolean
  resetKey: string
  items: T[]
  header?: React.ReactNode
  renderItem: (item: T) => React.ReactNode
  emptyText: string
}) {
  const { scrollRegionRef, sentinelRef, visibleCount, hasMore } = useRevealOnScroll({
    total: items.length,
    resetKey,
    enabled: open,
  })
  return (
    <div ref={scrollRegionRef} className="max-h-48 overflow-y-auto py-1">
      {header}
      {items.slice(0, visibleCount).map(renderItem)}
      {items.length === 0 ? (
        <div className="px-2.5 py-3 text-center text-xs text-muted-foreground">{emptyText}</div>
      ) : null}
      {hasMore ? <div ref={sentinelRef} aria-hidden className="h-px w-full shrink-0" /> : null}
    </div>
  )
}

/**
 * Headless form body. Renders title + pickers + action bar. Host chrome
 * (Dialog, Sheet, inline row) is supplied by the wrappers above.
 */
export function AppTaskCreatorForm({
  variant = 'modal',
  users,
  projects,
  currentUserEmail,
  defaultAssigneeId,
  defaultProjectId,
  defaultDueDate,
  onSubmit,
  onSubmitAndOpen,
  renderRelationFields,
  onAfterSubmit,
  onCancel,
  inputRef,
  submitRef,
  onEscalate,
  renderExtraFields,
  validateExtraFields,
  autoFocus = true,
}: AppTaskCreatorFormProps) {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [assigneeId, setAssigneeId] = React.useState<string | null>(null)
  const [assigneeTouched, setAssigneeTouched] = React.useState(false)
  const [projectId, setProjectId] = React.useState<string | null>(defaultProjectId ?? null)
  const [startDate, setStartDate] = React.useState<Date | null>(null)
  const [dueDate, setDueDate] = React.useState<Date | null>(defaultDueDate ?? null)
  const [hasDueTime, setHasDueTime] = React.useState(false)
  const [recurrenceRule, setRecurrenceRule] = React.useState<RecurrenceRule | null>(null)
  const [lead, setLead] = React.useState<AppTaskCreatorLead | null>(null)
  const [clients, setClients] = React.useState<AppTaskCreatorClient[]>([])
  const [expanded, setExpanded] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [extraErrorsVisible, setExtraErrorsVisible] = React.useState(false)
  // Synchronous mirror of `submitting`. React batches setSubmitting(true), so
  // two rapid taps within the same microtask both see submitting=false and
  // fire onSubmit twice — duplicate tasks. The ref reads/writes synchronously
  // and closes the window (79d74aa4).
  const submittingRef = React.useRef(false)

  const [assigneeOpen, setAssigneeOpen] = React.useState(false)
  const [assigneeSearch, setAssigneeSearch] = React.useState('')
  const [projectOpen, setProjectOpen] = React.useState(false)
  const [projectSearch, setProjectSearch] = React.useState('')

  const innerInputRef = React.useRef<HTMLInputElement>(null)
  const combinedInputRef = React.useMemo(() => mergeRefs<HTMLInputElement>(innerInputRef, inputRef), [inputRef])

  // Sync dueDate when defaultDueDate changes (host reopens creator with a
  // different day's default, e.g. user switches widget tab and reopens).
  React.useEffect(() => {
    setDueDate(defaultDueDate ?? null)
  }, [defaultDueDate])

  // Sync projectId when defaultProjectId changes (host switching context).
  React.useEffect(() => {
    setProjectId(defaultProjectId ?? null)
  }, [defaultProjectId])

  // A different project means a different set of extra fields — stale error
  // highlighting would point at rows that no longer apply.
  React.useEffect(() => {
    setExtraErrorsVisible(false)
  }, [projectId])

  // Resolve the "auto-assign to me" default. Re-runs when `users` resolves
  // asynchronously, so the current-user avatar snaps in even if `users` was
  // `[]` at mount. Skipped once the user manually changes the assignee.
  React.useEffect(() => {
    if (assigneeTouched) {
      return
    }
    const resolved =
      defaultAssigneeId ??
      (currentUserEmail
        ? users.find((u) => u.email.toLowerCase() === currentUserEmail.toLowerCase())?.id
        : undefined) ??
      null
    setAssigneeId(resolved)
  }, [assigneeTouched, defaultAssigneeId, currentUserEmail, users])

  const assignee = users.find((u) => u.id === assigneeId) ?? null
  const selectedProject = projects.find((p) => p.id === projectId) ?? null

  async function handleCreate(openSidebar = false): Promise<boolean> {
    const trimmed = title.trim()
    if (!trimmed || submittingRef.current) {
      return false
    }
    if (validateExtraFields && !validateExtraFields(projectId)) {
      setExtraErrorsVisible(true)
      return false
    }
    submittingRef.current = true
    setSubmitting(true)
    try {
      const input: AppTaskCreatorSubmitInput = {
        title: trimmed,
        description: description.trim() || undefined,
        assigneeId: assigneeId ?? undefined,
        projectId: projectId ?? undefined,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
        startDate: startDate ? startDate.toISOString() : undefined,
        hasDueTime,
        recurrenceRule,
        private: !projectId && clients.length === 0,
        ...(lead ? { leadRecordId: lead.recordId } : {}),
        ...(clients.length ? { organizations: clients } : {}),
      }
      if (openSidebar && onSubmitAndOpen) {
        await onSubmitAndOpen(input)
      } else if (openSidebar && onEscalate) {
        await onEscalate(input)
      } else {
        await onSubmit(input)
      }
      setExtraErrorsVisible(false)
      if (variant === 'inline') {
        setTitle('')
        setLead(null)
        setClients([])
        setDescription('')
        setStartDate(null)
        setDueDate(null)
        setHasDueTime(false)
        setRecurrenceRule(null)
        setExpanded(false)
        submittingRef.current = false
        setSubmitting(false)
        requestAnimationFrame(() => innerInputRef.current?.focus())
      } else {
        submittingRef.current = false
        setSubmitting(false)
        onAfterSubmit?.()
      }
      return true
    } catch {
      submittingRef.current = false
      setSubmitting(false)
      return false
    }
  }

  const latestSubmit = React.useRef(handleCreate)
  latestSubmit.current = handleCreate
  React.useImperativeHandle(
    submitRef,
    () => ({
      submit: () => latestSubmit.current(),
      hasContent: () => !!innerInputRef.current?.value.trim(),
    }),
    [],
  )

  const filteredUsers = assigneeSearch
    ? users.filter((u) => ((u.name ?? '') + ' ' + u.email).toLowerCase().includes(assigneeSearch.toLowerCase()))
    : users

  const filteredProjects = projectSearch
    ? projects.filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
    : projects

  // Popover side varies by variant: modal/panel pickers open above the
  // trigger (compact bottom-action layout); inline opens below (pills sit
  // beneath the title, list content sits above).
  const popoverSide = variant === 'inline' ? 'bottom' : 'top'

  // Shared windowed list bodies — used by both the pill pickers (modal/panel)
  // and the icon-trigger pickers (inline). One instance renders at a time
  // (the variants are mutually exclusive), so sharing the element is safe.
  const assigneeListBody = (
    <RevealList
      open={assigneeOpen}
      resetKey={`${assigneeOpen}|${assigneeSearch}`}
      items={filteredUsers}
      emptyText="No matches"
      header={
        assignee ? (
          <button
            type="button"
            onClick={() => {
              setAssigneeId(null)
              setAssigneeTouched(true)
              setAssigneeSearch('')
              setAssigneeOpen(false)
            }}
            className="flex items-center gap-2 w-full px-2.5 py-2 text-sm hover:bg-muted transition-colors text-left text-muted-foreground">
            Remove assignee
          </button>
        ) : null
      }
      renderItem={(u) => (
        <button
          key={u.id}
          type="button"
          onClick={() => {
            setAssigneeId(u.id)
            setAssigneeTouched(true)
            setAssigneeSearch('')
            setAssigneeOpen(false)
          }}
          className={cn(
            'flex items-center gap-2 w-full px-2.5 py-2 text-sm hover:bg-muted transition-colors text-left',
            u.id === assigneeId && 'bg-primary/10',
          )}>
          <Avatar className="h-6 w-6 shrink-0">
            {u.image ? <AvatarImage src={u.image} alt="" /> : null}
            <AvatarFallback className="text-[10px]">{userInitials(u)}</AvatarFallback>
          </Avatar>
          <span className="truncate">{u.name ?? u.email}</span>
        </button>
      )}
    />
  )

  const projectListBody = (
    <RevealList
      open={projectOpen}
      resetKey={`${projectOpen}|${projectSearch}`}
      items={filteredProjects}
      emptyText="No matches"
      header={
        selectedProject ? (
          <button
            type="button"
            onClick={() => {
              setProjectId(null)
              setProjectSearch('')
              setProjectOpen(false)
            }}
            className="flex items-center gap-2 w-full px-2.5 py-2 text-sm hover:bg-muted transition-colors text-left text-muted-foreground">
            Remove project
          </button>
        ) : null
      }
      renderItem={(p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => {
            setProjectId(p.id)
            setProjectSearch('')
            setProjectOpen(false)
          }}
          className={cn(
            'flex items-center gap-2 w-full px-2.5 py-2 text-sm hover:bg-muted transition-colors text-left',
            p.id === projectId && 'bg-primary/10',
          )}>
          <FolderKanban className="h-4 w-4 shrink-0" />
          <span className="truncate">{p.name}</span>
        </button>
      )}
    />
  )

  const canSubmit = !!title.trim() && !submitting

  const titleInput = (
    <input
      ref={combinedInputRef}
      autoFocus={autoFocus}
      type="text"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          handleCreate()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onCancel?.()
        }
      }}
      placeholder="Task name..."
      className={cn(
        'w-full bg-transparent outline-none placeholder:text-muted-foreground/50',
        variant === 'panel' ? 'typo-heading-lg' : 'text-base',
        variant !== 'inline' && 'mb-3',
      )}
    />
  )

  const assigneePicker = (
    <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
      <PopoverTrigger
        // preventDefault on mousedown stops the title input from blurring
        // before the popover opens on tap — keeps the mobile keyboard
        // composition intact and turns the previous 2-tap interaction into
        // 1 tap (b769f2eb). preventDefault on mousedown does not block the
        // subsequent click event Radix listens for.
        onMouseDown={(e) => e.preventDefault()}
        className={cn(
          'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors flex-1 min-w-0',
          assignee ? 'bg-primary/10' : 'bg-muted',
        )}>
        {assignee ? (
          <>
            <Avatar className="h-5 w-5 shrink-0">
              {assignee.image ? <AvatarImage src={assignee.image} alt="" /> : null}
              <AvatarFallback className="text-[10px]">{userInitials(assignee)}</AvatarFallback>
            </Avatar>
            <span className="truncate text-left">
              <span className="text-muted-foreground text-xs block leading-tight">Assigned to</span>
              <span className="text-foreground leading-tight">{assignee.name}</span>
            </span>
          </>
        ) : (
          <>
            <User className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate text-left">
              <span className="text-muted-foreground text-xs block leading-tight">Assigned to</span>
              <span className="text-muted-foreground leading-tight block">No one</span>
            </span>
          </>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" side={popoverSide} sideOffset={8} className="w-64 p-1">
        <input
          autoFocus
          type="text"
          value={assigneeSearch}
          onChange={(e) => setAssigneeSearch(e.target.value)}
          placeholder="Search people..."
          className="w-full px-2.5 py-2 text-sm bg-transparent outline-none border-b border-border placeholder:text-muted-foreground/50"
        />
        {assigneeListBody}
      </PopoverContent>
    </Popover>
  )

  const hasAnyDate = !!dueDate || !!startDate
  const dueDatePicker = (
    <DateRecurrencePicker
      dueDate={dueDate}
      startDate={startDate}
      hasDueTime={hasDueTime}
      recurrenceRule={recurrenceRule}
      onDueDateChange={setDueDate}
      onStartDateChange={setStartDate}
      onHasDueTimeChange={setHasDueTime}
      onRecurrenceChange={setRecurrenceRule}
      trigger={
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors min-w-0',
            hasAnyDate ? 'bg-primary/10' : 'bg-muted',
          )}>
          <CalendarIcon className={cn('h-4 w-4 shrink-0', hasAnyDate ? 'text-foreground' : 'text-muted-foreground')} />
          {hasAnyDate ? (
            <span className="truncate text-left">
              <span className="text-muted-foreground text-xs block leading-tight">Due date</span>
              <span className="text-foreground leading-tight block">
                {dueDate ? formatDueLabel(dueDate) : 'No date'}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Due date</span>
          )}
          {recurrenceRule ? <Repeat className="h-3 w-3 shrink-0 text-primary" /> : null}
        </button>
      }
    />
  )

  // Row-trigger project picker used by the modal's "expanded" section — keeps
  // parity with the previous visual (text button, no pill).
  const projectPickerRow = (
    <Popover open={projectOpen} onOpenChange={setProjectOpen}>
      <PopoverTrigger asChild>
        <TaskRelationTrigger icon={selectedProject ? <FolderKanban className="h-3.5 w-3.5 shrink-0" /> : undefined}>
          {selectedProject?.name ?? 'Project'}
        </TaskRelationTrigger>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={4} className="w-64 p-1">
        <input
          autoFocus
          type="text"
          value={projectSearch}
          onChange={(e) => setProjectSearch(e.target.value)}
          placeholder="Search projects..."
          className="w-full px-2.5 py-2 text-sm bg-transparent outline-none border-b border-border placeholder:text-muted-foreground/50"
        />
        {projectListBody}
      </PopoverContent>
    </Popover>
  )

  const descriptionField = (
    <textarea
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      placeholder="Add a description..."
      rows={variant === 'panel' ? 4 : 2}
      className="w-full text-sm bg-muted/50 rounded-lg px-3 py-2 outline-none resize-none placeholder:text-muted-foreground/40"
    />
  )

  const extraFields = renderExtraFields ? renderExtraFields({ projectId, showErrors: extraErrorsVisible }) : null

  const relationFields = renderRelationFields?.({
    title,
    description,
    lead,
    clients,
    onLeadChange: setLead,
    onClientsChange: setClients,
  })

  const selfId =
    defaultAssigneeId ?? users.find((user) => user.email.toLowerCase() === currentUserEmail?.toLowerCase())?.id
  const privateHeader =
    !projectId && clients.length === 0 && !!selfId && assigneeId === selfId ? (
      <>
        <span className="text-muted-foreground/40">·</span>
        <Lock className="h-3 w-3 text-muted-foreground/60" />
        <span className="text-xs text-muted-foreground/60">Private to you</span>
      </>
    ) : null

  const createButton = (
    <button
      type="button"
      onClick={() => void handleCreate()}
      disabled={!canSubmit}
      className="flex items-center gap-1 text-sm font-semibold text-primary disabled:opacity-30 transition-opacity">
      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
      Create
    </button>
  )

  // ---------- Variant: inline ----------
  if (variant === 'inline') {
    // Row-style pickers: icon-only triggers that open the same popover content
    // as the pill variants above. Kept inline so the popover state (assigneeOpen,
    // projectOpen, dueDate...) is shared with the bigger variants.
    const assigneeIconTrigger = (
      <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
        <PopoverTrigger
          onMouseDown={(e) => e.preventDefault()}
          className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted transition-colors"
          aria-label="Assignee">
          {assignee ? (
            <Avatar className="h-6 w-6">
              {assignee.image ? <AvatarImage src={assignee.image} alt="" /> : null}
              <AvatarFallback className="text-[10px]">{userInitials(assignee)}</AvatarFallback>
            </Avatar>
          ) : (
            <User className="h-4 w-4 text-muted-foreground" />
          )}
        </PopoverTrigger>
        <PopoverContent align="end" side={popoverSide} sideOffset={8} className="w-64 p-1">
          <input
            autoFocus
            type="text"
            value={assigneeSearch}
            onChange={(e) => setAssigneeSearch(e.target.value)}
            placeholder="Search people..."
            className="w-full px-2.5 py-2 text-sm bg-transparent outline-none border-b border-border placeholder:text-muted-foreground/50"
          />
          {assigneeListBody}
        </PopoverContent>
      </Popover>
    )

    const dueDateIconTrigger = (
      <DateRecurrencePicker
        dueDate={dueDate}
        startDate={startDate}
        hasDueTime={hasDueTime}
        recurrenceRule={recurrenceRule}
        onDueDateChange={setDueDate}
        onStartDateChange={setStartDate}
        onHasDueTimeChange={setHasDueTime}
        onRecurrenceChange={setRecurrenceRule}
        trigger={
          <button
            type="button"
            aria-label="Due date"
            className={cn(
              'flex h-7 items-center gap-1 rounded-full px-2 text-xs transition-colors',
              hasAnyDate ? 'text-foreground bg-primary/10' : 'text-muted-foreground hover:bg-muted',
            )}>
            <CalendarIcon className="h-4 w-4" />
            {hasAnyDate ? <span>{formatDueLabel(dueDate)}</span> : null}
          </button>
        }
      />
    )

    const projectIconTrigger = (
      <Popover open={projectOpen} onOpenChange={setProjectOpen}>
        <PopoverTrigger
          aria-label="Project"
          className={cn(
            'flex h-7 items-center gap-1 rounded-full px-2 text-xs transition-colors',
            selectedProject ? 'text-foreground bg-primary/10' : 'text-muted-foreground hover:bg-muted',
          )}>
          <FolderKanban className="h-4 w-4" />
          {selectedProject ? <span className="truncate max-w-[120px]">{selectedProject.name}</span> : null}
        </PopoverTrigger>
        <PopoverContent align="end" side={popoverSide} sideOffset={8} className="w-64 p-1">
          <input
            autoFocus
            type="text"
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            placeholder="Search projects..."
            className="w-full px-2.5 py-2 text-sm bg-transparent outline-none border-b border-border placeholder:text-muted-foreground/50"
          />
          {projectListBody}
        </PopoverContent>
      </Popover>
    )

    return (
      <div>
        <div className="group flex items-center gap-2 px-4 py-3 md:py-2 hover:bg-muted/30 transition-colors">
          <div className="h-5 w-5 shrink-0 rounded-full border-2 border-border/60" aria-hidden />
          <input
            ref={combinedInputRef}
            autoFocus={autoFocus}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleCreate()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                onCancel?.()
              }
            }}
            placeholder="Write a task name..."
            className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground/50"
          />
          <div className="flex items-center gap-1 shrink-0">
            {dueDateIconTrigger}
            {!defaultProjectId ? projectIconTrigger : null}
            {assigneeIconTrigger}
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={!canSubmit}
              aria-label="Create task"
              className="flex h-7 w-7 items-center justify-center rounded-md text-primary disabled:opacity-30 hover:bg-primary/10 transition-colors">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {extraFields ? <div className="px-4 pb-3 pl-11">{extraFields}</div> : null}
      </div>
    )
  }

  // ---------- Variant: panel ----------
  if (variant === 'panel') {
    return (
      <div className="flex h-full flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-muted-foreground">Task</span>
            {privateHeader}
          </div>
          {titleInput}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex items-center gap-3">
            {assigneePicker}
            {dueDatePicker}
          </div>
          <div>{projectPickerRow}</div>
          {relationFields}
          <div>{descriptionField}</div>
          {extraFields}
        </div>
        <div className="border-t border-border px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled
              title="Attach image (coming soon)"
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
              <ImageIcon className="h-[18px] w-[18px]" />
            </button>
            <button
              type="button"
              disabled
              title="Attach file (coming soon)"
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
              <Paperclip className="h-[18px] w-[18px]" />
            </button>
          </div>
          {createButton}
        </div>
      </div>
    )
  }

  // ---------- Variant: modal (default) ----------
  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-muted-foreground">Task</span>
        {privateHeader}
      </div>
      {titleInput}
      <div className="flex items-center gap-3 mb-3">
        {assigneePicker}
        {dueDatePicker}
      </div>
      {expanded ? (
        <div className="space-y-1 mb-3 border-t border-border pt-3">
          {projectPickerRow}
          {relationFields}
          <div>{descriptionField}</div>
        </div>
      ) : null}
      {extraFields ? <div className="mb-3">{extraFields}</div> : null}
      <div className="flex items-center justify-between pt-1 border-t border-border">
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled
            title="Attach image (coming soon)"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
            <ImageIcon className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            disabled
            title="Attach file (coming soon)"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
            <Paperclip className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? 'Hide details' : 'Show details'}
            className={cn(
              'p-2 rounded-lg transition-colors',
              expanded ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground',
            )}>
            <ChevronUp className={cn('h-[18px] w-[18px] transition-transform', expanded && 'rotate-180')} />
          </button>
          {(onSubmitAndOpen || onEscalate) && (
            <button
              type="button"
              onClick={() => void handleCreate(true)}
              disabled={!canSubmit}
              aria-label="Create and open in sidebar"
              title="Create and open in sidebar"
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <PanelRightOpen className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
        {createButton}
      </div>
    </>
  )
}

// ============================================================================
// Wrappers
// ============================================================================

/**
 * Dialog (desktop) / bottom Sheet (mobile) wrapper around `AppTaskCreatorForm`.
 * Back-compat: existing callers (HQ's `TaskCreatorProvider`) see no API change.
 */
export function AppTaskCreator({ open, onOpenChange, ...rest }: AppTaskCreatorProps) {
  const isMobile = useIsMobile()

  // Only mount the form while open — gives a fresh state on every open and
  // avoids flashing empty fields during the close animation.
  const form = open ? (
    <AppTaskCreatorForm
      variant="modal"
      {...rest}
      onAfterSubmit={() => onOpenChange(false)}
      onCancel={() => onOpenChange(false)}
    />
  ) : null

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-5 pt-3">
          <SheetTitle className="sr-only">Create task</SheetTitle>
          {form}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-5">
        <DialogTitle className="sr-only">Create task</DialogTitle>
        {form}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Inline row creator. Idle: "Add a task" placeholder row. Expands in place
 * on click / focus and exposes an imperative `focus()` handle for host
 * buttons (e.g. header "New Task") that should route to the inline row.
 *
 * Escape collapses to idle. Click-outside the expanded form collapses to
 * idle only when the title input is empty — work in progress is preserved.
 */
export const AppTaskCreatorInline = React.forwardRef<AppTaskCreatorInlineHandle, AppTaskCreatorInlineProps>(
  ({ className, idlePlaceholder = 'Add a task', hideIdle = false, ...formProps }, ref) => {
    const [expanded, setExpanded] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const submitHandleRef = React.useRef<AppTaskCreatorFormSubmitHandle>(null)

    const focus = React.useCallback(() => {
      setExpanded(true)
      // Expansion mounts the form; defer focus to the following frame so the
      // input is in the DOM when we call focus().
      requestAnimationFrame(() => inputRef.current?.focus())
    }, [])

    React.useImperativeHandle(ref, () => ({ focus }), [focus])

    // Click outside the expanded row: save if the user typed something, otherwise
    // collapse. Ignore clicks into Radix popover portals (assignee / due-date /
    // project dropdowns render in a portal, so they're not inside `containerRef`).
    React.useEffect(() => {
      if (!expanded) {
        return
      }
      const onMouseDown = (e: MouseEvent) => {
        const target = e.target
        if (!(target instanceof Node)) {
          return
        }
        if (containerRef.current?.contains(target)) {
          return
        }
        const el = target instanceof Element ? target : null
        if (el?.closest('[data-slot="popover-content"]')) {
          return
        }
        if (el?.closest('[data-radix-popper-content-wrapper]')) {
          return
        }
        const handle = submitHandleRef.current
        if (handle?.hasContent()) {
          void handle.submit().then(() => setExpanded(false))
          return
        }
        setExpanded(false)
      }
      document.addEventListener('mousedown', onMouseDown)
      return () => document.removeEventListener('mousedown', onMouseDown)
    }, [expanded])

    if (!expanded && hideIdle) {
      return <div ref={containerRef} className={className} />
    }

    return (
      <div ref={containerRef} className={className}>
        {expanded ? (
          <AppTaskCreatorForm
            variant="inline"
            {...formProps}
            inputRef={inputRef}
            submitRef={submitHandleRef}
            onCancel={() => setExpanded(false)}
          />
        ) : (
          <button
            type="button"
            onClick={focus}
            className="group flex items-center gap-2 w-full px-4 py-3 md:py-2 text-left text-sm text-muted-foreground/70 hover:bg-muted/30 transition-colors">
            <Plus className="h-4 w-4" />
            <span>{idlePlaceholder}</span>
          </button>
        )}
      </div>
    )
  },
)
AppTaskCreatorInline.displayName = 'AppTaskCreatorInline'
